import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import supabase from '../../supabase/supabase';
import { CHAT_TABLE } from '../Chat/chatConstants';
import { getClientId } from '../../utils/clientIdentity';

const { FiTv, FiUsers, FiMessageSquare, FiAlertTriangle, FiSend, FiActivity } = FiIcons;

export default function AdminOverview({ settings, onUpdateSettings, messages, restrictions, onlineCount }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [sendingAnnouncement, setSendingAnnouncement] = useState(false);
  const [statusMsg, setStatusMsg] = useState({ type: '', text: '' });

  const LOGO_URL = "https://media-manager-c.questera.ai/greta-media/786069decac80ebfda9758781ecad48f576fd486e338363dff55357567d6916b51c713fb933d539c7b707900e265a4cf/images/aW1hZ2UvcG5n/05487cee64958a73423d5bdd3c770274.png";

  const totalMessages = messages.length;
  const activeBans = restrictions.filter(r => r.active && r.action === 'ban').length;

  useEffect(() => {
    let mounted = true;
    if (!settings?.stream_url || !settings?.is_active) {
      setIsPlaying(false);
      return;
    }
    const video = videoRef.current;
    if (!video) return;
    
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', 'true');

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = settings.stream_url;
      video.addEventListener('loadedmetadata', () => {
        if (mounted) {
          setIsPlaying(true);
          video.play().catch(() => setIsPlaying(false));
        }
      }, { once: true });
    } else if (Hls.isSupported()) {
      const hls = new Hls({
        liveSyncDurationCount: 3,
        liveMaxLatencyDurationCount: 6,
        enableWorker: true
      });
      hlsRef.current = hls;
      hls.loadSource(settings.stream_url);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (mounted) {
          setIsPlaying(true);
          video.play().catch(() => setIsPlaying(false));
        }
      });
      hls.on(Hls.Events.ERROR, () => {
        if (mounted) setIsPlaying(false);
      });
    }

    return () => {
      mounted = false;
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [settings?.stream_url, settings?.is_active]);

  const handleSendAnnouncement = async (e) => {
    e.preventDefault();
    if (!announcement.trim() || sendingAnnouncement) return;
    setSendingAnnouncement(true);
    setStatusMsg({ type: '', text: '' });

    try {
      const { error } = await supabase.from(CHAT_TABLE).insert({
        nickname: '📢 ANUNCIO OFICIAL',
        content: announcement.trim(),
        sender_id: getClientId(),
        is_private: false
      });
      if (error) throw error;
      setAnnouncement('');
      setStatusMsg({ type: 'success', text: 'Anuncio publicado con éxito en el chat general.' });
    } catch (err) {
      setStatusMsg({ type: 'error', text: 'No se pudo publicar el anuncio.' });
    } finally {
      setSendingAnnouncement(false);
      setTimeout(() => setStatusMsg({ type: '', text: '' }), 4000);
    }
  };

  const toggleStreamStatus = async () => {
    try {
      await onUpdateSettings({ is_active: !settings.is_active });
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Grid de Métricas de la Plataforma */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <div className="rounded-2xl border border-white/10 bg-[#111] p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-400 sm:text-sm">Espectadores</span>
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-500/10 text-green-400">
              <SafeIcon icon={FiUsers} size={18} />
            </span>
          </div>
          <div className="mt-4 flex items-baseline gap-2">
            <span className="text-2xl font-bold tracking-tight text-white sm:text-3xl">{onlineCount}</span>
            <span className="flex items-center gap-1 text-[10px] font-semibold text-green-400">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-green-400" /> En vivo
            </span>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#111] p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-400 sm:text-sm">Mensajes Totales</span>
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-500/10 text-red-500">
              <SafeIcon icon={FiMessageSquare} size={18} />
            </span>
          </div>
          <div className="mt-4">
            <span className="text-2xl font-bold tracking-tight text-white sm:text-3xl">{totalMessages}</span>
            <p className="text-[10px] text-gray-500 mt-1">Historial activo</p>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#111] p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-400 sm:text-sm">Bloqueados (Bans)</span>
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-yellow-500/10 text-yellow-500">
              <SafeIcon icon={FiAlertTriangle} size={18} />
            </span>
          </div>
          <div className="mt-4">
            <span className="text-2xl font-bold tracking-tight text-white sm:text-3xl">{activeBans}</span>
            <p className="text-[10px] text-gray-500 mt-1">Indefinidos</p>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#111] p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-400 sm:text-sm">Estado Señal</span>
            <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${settings?.is_active ? 'bg-red-500/10 text-red-500' : 'bg-gray-500/10 text-gray-400'}`}>
              <SafeIcon icon={FiTv} size={18} />
            </span>
          </div>
          <div className="mt-4 flex items-center justify-between">
            <span className={`text-sm font-bold sm:text-lg ${settings?.is_active ? 'text-red-500' : 'text-gray-500'}`}>
              {settings?.is_active ? 'ENCENDIDO' : 'PAUSADO'}
            </span>
            <button
              onClick={toggleStreamStatus}
              className={`rounded px-2 py-1 text-[10px] font-bold text-white transition-all ${
                settings?.is_active ? 'bg-gray-800 hover:bg-gray-700' : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              {settings?.is_active ? 'Pausar' : 'Iniciar'}
            </button>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-12">
        {/* Monitor de Transmisión en Vivo */}
        <div className="lg:col-span-7 rounded-2xl border border-white/10 bg-[#111] overflow-hidden flex flex-col">
          <div className="border-b border-white/10 px-4 py-3.5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiActivity} className="text-red-500 animate-pulse" />
              <h3 className="font-semibold text-white text-sm sm:text-base">Monitor de Transmisión</h3>
            </div>
            {settings?.is_active && (
              <span className="rounded-full bg-red-600/10 px-2 py-0.5 text-[10px] font-bold text-red-500 flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-red-600 animate-ping" /> ONLINE
              </span>
            )}
          </div>
          
          <div className="relative aspect-video bg-black flex-1 flex items-center justify-center">
            {settings?.is_active ? (
              <video 
                ref={videoRef} 
                muted 
                controls 
                playsInline 
                webkit-playsinline="true"
                className="w-full h-full object-contain" 
              />
            ) : (
              <div className="text-center p-4 flex flex-col items-center">
                <img 
                  src={LOGO_URL} 
                  alt="Logo Hacienda El Gallo" 
                  className="w-48 h-48 md:w-56 md:h-56 mb-4 opacity-50 grayscale object-contain" 
                />
                <p className="text-base font-bold text-gray-300">La transmisión está pausada</p>
                <p className="text-xs text-gray-500 mt-1 max-w-[250px]">Actívala para visualizar la señal en vivo para los espectadores.</p>
              </div>
            )}
          </div>
          <div className="p-4 bg-black/40 border-t border-white/5 text-xs text-gray-400">
            <span className="font-semibold text-gray-300">Señal activa:</span> {settings?.stream_url || 'Ninguna'}
          </div>
        </div>

        {/* Emisión de Anuncios y Acciones Rápidas */}
        <div className="lg:col-span-5 flex flex-col justify-between rounded-2xl border border-white/10 bg-[#111] p-5 space-y-6">
          <div>
            <h3 className="font-semibold text-white text-sm sm:text-base mb-2">Anuncio del Sistema</h3>
            <p className="text-xs text-gray-500">
              Envía un mensaje destacado que aparecerá de inmediato en el chat para todos los espectadores conectados.
            </p>
            <form onSubmit={handleSendAnnouncement} className="mt-4 space-y-3">
              <textarea
                value={announcement}
                maxLength={500}
                required
                onChange={(e) => setAnnouncement(e.target.value)}
                placeholder="Escribe un anuncio oficial... (Ej: ¡La gran final está por comenzar!)"
                className="w-full h-24 rounded-xl border border-white/10 bg-white/5 p-3 text-sm text-white placeholder:text-gray-600 focus:border-red-600 focus:outline-none resize-none"
              />
              {statusMsg.text && (
                <p className={`rounded-lg p-2.5 text-xs border ${
                  statusMsg.type === 'success' ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'
                }`}>
                  {statusMsg.text}
                </p>
              )}
              <button
                type="submit"
                disabled={sendingAnnouncement || !announcement.trim()}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-red-600 py-2.5 text-xs font-bold text-white transition-colors hover:bg-red-700 disabled:opacity-50"
              >
                <SafeIcon icon={FiSend} />
                {sendingAnnouncement ? 'Enviando...' : 'Publicar Anuncio'}
              </button>
            </form>
          </div>

          <div className="pt-4 border-t border-white/5">
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Consejos de Gestión</h4>
            <ul className="text-xs text-gray-500 space-y-1.5 list-disc pl-4">
              <li>Pausar la señal muestra la pantalla de espera con el logo a los usuarios.</li>
              <li>El chat de moderación te permite expulsar usuarios de forma temporal (15 min) o permanente.</li>
              <li>Los anuncios del sistema se guardan con el apodo de alta prioridad y resaltados en rojo.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}